import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  inject,
  OnDestroy,
  OnInit
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import DropDownOptions from '@approot/modals/trade-capture/schedule-reports/report-dropdown-options.json';
import {
  PNLBlotterGridStateKey,
  PNLBlotterTimeZoneStateKey
} from '@approot/models';
import { AgGridService, UserStorageService } from '@approot/services';
import { clone, isFeatureEnabled, SubscriptionSink } from '@approot/utilities';
import {
  AgGridEvent,
  ColDef,
  ColumnState,
  GridReadyEvent,
  SideBarDef
} from 'ag-grid-community';
import { isEqual } from 'lodash';
import {
  auditTime,
  debounceTime,
  distinctUntilChanged,
  take
} from 'rxjs/operators';
import { RunningState } from '../shared/utils';
import { applyColumnState } from './pnl-blotter.funcs';
import { PNLBlotterService } from './pnl-blotter.service';
import { PNLBlotterState } from './pnl-blotter.types';
import { PNLPivotService } from './pnl-pivot-table/pnl-pivot.service';

@Component({
  selector: 'app-pnl-blotter',
  templateUrl: './pnl-blotter.component.html',
  styleUrls: ['./pnl-blotter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PNLBlotterComponent implements OnInit, OnDestroy {
  public sideBarDef: SideBarDef;
  public state: PNLBlotterState = {};
  public entities: string[] = [];
  public runningState: RunningState = RunningState.Idle;
  public RunningState = RunningState;
  public readonly defaultColDef: ColDef = {
    sortable: true,
    enableRowGroup: true,
    resizable: true
  };
  public headers: any[] = [];
  public rowData: any[] = [];
  public readonly overlayNoRowsTemplate =
    '<span style="padding: 10px; border: 2px solid #444;">No PNL loaded</span>';
  public form: FormGroup = new FormGroup({
    entity: new FormControl<string>(null),
    timezone: new FormControl<string>('Europe/London'),
    start_date: new FormControl<string>(null, Validators.required),
    start_time: new FormControl<string>(null, Validators.required),
    end_date: new FormControl<string>(null),
    end_time: new FormControl<string>(null)
  });
  public timezones: string[] = DropDownOptions.timezones;
  public enablePnlPivotBlotter = isFeatureEnabled(
    'enablePnlPivotBlotter',
    false
  );
  private subsink = new SubscriptionSink();
  private agGridService = inject(AgGridService);
  private changeRef = inject(ChangeDetectorRef);
  private pnlBlotterService = inject(PNLBlotterService);
  private userStorageService = inject(UserStorageService);
  private pnlPivotService = inject(PNLPivotService);

  public constructor() {
    this.sideBarDef = this.agGridService.sideBarDef;
    this.subsink.sink = this.form.valueChanges
      .pipe(
        debounceTime(250),
        distinctUntilChanged((a, b) => isEqual(a, b))
      )
      .subscribe(v => this.pnlBlotterService.update({ selectedEntry: v }));

    this.subsink.sink = this.form
      .get('timezone')
      .valueChanges.pipe(distinctUntilChanged((a, b) => isEqual(a, b)))
      .subscribe(tz =>
        this.userStorageService.setItem(PNLBlotterTimeZoneStateKey, tz)
      );

    const tz =
      this.userStorageService.getItem(PNLBlotterTimeZoneStateKey) ||
      'Europe/London';
    this.form.get('timezone').setValue(tz, { emitEvent: false });
  }

  public ngOnInit(): void {
    // initial state.  Although we resubscribe to stateChanged in onGridReady, we
    // get it immediately (i.e., no `auditTime()`) so that the form is populated with
    // the correct entity, otherwise there's an annoying delay.
    this.subsink.sink = this.pnlBlotterService.stateChanged
      .pipe(take(1))
      .subscribe({
        next: state => this.updateOnStateChange(state)
      });
  }

  public ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  public onGridReady(params: GridReadyEvent) {
    this.subsink.sink = this.pnlBlotterService.stateChanged
      .pipe(
        auditTime(250) // Don't update too often
      )
      .subscribe({
        next: state => this.updateOnStateChange(state)
      });

    const columnState = this.userStorageService.getItem<ColumnState[]>(
      PNLBlotterGridStateKey
    );
    if (columnState) {
      applyColumnState(params.columnApi, columnState);
    }
  }

  public canRunReport(): boolean {
    const form = this.form.getRawValue();
    const validStartDate = /^\d\d\d\d-\d\d-\d\d$/.test(form['start_date']);
    const validStartTime = /^\d\d:\d\d$/.test(form['start_time']);
    const validEndDate = /^\d\d\d\d-\d\d-\d\d$/.test(form['end_date']);
    const validEndTime = /^\d\d:\d\d$/.test(form['end_time']);
    if (!(validStartDate && validStartTime)) {
      return false;
    }
    if (form['end_date'] && !validEndDate) {
      return false;
    }
    if (form['end_time'] && !validEndTime) {
      return false;
    }
    if (validEndTime && !validEndDate) {
      return false;
    }
    if (form['entity'] == null) {
      return false;
    }
    return true;
  }

  public runReport() {
    const form = this.form.getRawValue();
    if (!this.canRunReport()) {
      return;
    }

    this.pnlBlotterService.runReport(form);
  }

  public cancelReport() {
    this.pnlBlotterService.cancelReport();
  }

  public gridStateChanged(event: AgGridEvent): void {
    const allState = event.columnApi.getColumnState();
    if (allState) {
      this.userStorageService.setItem(PNLBlotterGridStateKey, allState);
    } else {
      this.userStorageService.removeItem(PNLBlotterGridStateKey);
    }
  }

  private updateOnStateChange(state: PNLBlotterState) {
    this.state = state;
    // We don't want to update the grid if the data unchanged
    if (this.rowData.length !== state.rows.length) {
      this.rowData = state.rows;
    }
    if (this.entities.length !== state.entities.length) {
      this.entities = clone(state.entities).sort((a, b) =>
        a.localeCompare(b, 'en', { sensitivity: 'accent' })
      );
    }
    if (this.headers.length !== state.headers.length) {
      this.headers = state.headers;
    }
    this.form
      .get('entity')
      .setValue(state.selectedEntry?.entity, { emitEvent: false });
    this.changeRef.markForCheck();
  }

  public displayPnlPivotBlotter(): void {
    this.pnlPivotService.displayPnlPivotBlotter();
  }
}
